import { useCallback, useState } from "react";
import { authStorageService } from "@/features/auth/services/storage.service";
import {
  type SessionState,
  type AuthErrorResponse,
  type AuthWith2FactorResponse,
  type AuthMissingCellPhoneResponse,
  isMissingCellPhoneNumber,
  isTwoFactorRequired,
  isFirstSignIn,
  AuthResponse,
  RefreshTokenResponse,
  AuthCheck2FAResponse,
  isAuthLoginResponse,
  AuthLoginResponse,
  APP_APPROVAL_CHANNEL,
} from "@/features/auth/types/auth.type";
import type { Organization } from "@/features/auth/types/organization.type";
import type { Plan } from "@/features/auth/types/plan.type";
import { markLegalComplianceAccepted } from "@/features/auth/utils/auth.utils";
import { queryClient } from "@/shared/lib/query-client";
import type { User, UserResponse } from "@/shared/types/";

const initialState: SessionState = {
  isAuthenticating: false,
  isWaitingForUser: false,
  isWaitingForPlan: false,
  isWaitingForOrganization: false,
  isWaitingForCmsPages: false,
  user: null,
  plan: null,
  organization: null,
  isTwoFactorRequired: false,
  isCellPhoneRequired: false,
  isFirstSignInRequired: false,
  isLegalComplianceRequired: false,
  isAuthenticated: false,
  twoFactorData: {
    otp_challenge: null,
    channel: null,
    timestamp: null,
    approval: null,
  },
  isAuthFailed: false,
  error: {
    title: null,
    description: null,
  },
};

export const useAuthState = () => {
  const [session, setSession] = useState<SessionState>(initialState);

  const updateSession = useCallback((payload: Partial<SessionState>) => {
    setSession((prevState) => ({ ...prevState, ...payload }));
  }, []);

  const onAuthenticating = useCallback((isAuthenticating: boolean): void => updateSession({ isAuthenticating }), []);

  const onWaitingForUser = useCallback(({ access_token, expires_in }: AuthLoginResponse): void => {
    queryClient.getQueryCache().clear();

    authStorageService.saveAuthToken(access_token, expires_in);
    updateSession({ isWaitingForUser: true, isAuthenticating: true, isAuthenticated: false });
  }, []);

  const onTwoFactorRequired = useCallback((data: AuthWith2FactorResponse): void => updateSession({
    isTwoFactorRequired: true,
    isCellPhoneRequired: false,
    twoFactorData: {
      otp_challenge: data.challenge,
      channel: data.channel,
      timestamp: data.code_sent_at ?? null,
      approval: data.channel === APP_APPROVAL_CHANNEL && data.approval_number && data.poll_secret
        ? { number: data.approval_number, pollSecret: data.poll_secret, expiresIn: data.expires_in ?? 120 }
        : null,
    },
  }), []);

  const onCellPhoneRequired = useCallback((data: AuthMissingCellPhoneResponse): void => updateSession({
    isTwoFactorRequired: false,
    isCellPhoneRequired: true,
    error: {
      title: data.error.title ?? null,
      description: data.error.description ?? null,
    },
  }), []);

  const onFirstSignInRequired = useCallback((): void => updateSession({
    isTwoFactorRequired: false,
    isCellPhoneRequired: false,
    isFirstSignInRequired: true,
  }), []);

  const onUserDataReceived = useCallback((user: UserResponse): void => {
    // useUserQuery replays every `["user"]` cache write into this handler.
    // Once the chain has moved past its initial pause (organization/plan/CMS
    // loading under way, or already authenticated), a later cache write must
    // only refresh the cached profile — never restart the chain or
    // re-surface a requirement that was already resolved.
    setSession((previousSession) => {
      const hasResolvedInitialPause =
        previousSession.isWaitingForOrganization ||
        previousSession.isWaitingForPlan ||
        previousSession.isWaitingForCmsPages ||
        previousSession.isAuthenticated;

      if (hasResolvedInitialPause) {
        return { ...previousSession, user };
      }

      const isLegalComplianceRequired = user.employee.legal_compliance_acceptance_required === true;

      return {
        ...initialState,
        user,
        isLegalComplianceRequired,
        isAuthenticating: !isLegalComplianceRequired,
        isWaitingForOrganization: !isLegalComplianceRequired,
      };
    });
  }, []);

  const onLegalComplianceCompleted = useCallback((employeeUpdates: Partial<User> = {}): void => {
    setSession((previousSession) => ({
      ...previousSession,
      user: previousSession.user && markLegalComplianceAccepted(previousSession.user, employeeUpdates),
      isLegalComplianceRequired: false,
      isWaitingForOrganization: true,
    }));
  }, []);

  // Défense en profondeur : `error` peut être undefined ou d'une forme
  // inattendue quand une réponse non-JSON traverse la couche service
  // (page d'erreur proxy/CDN, corps vide).
  const onError = useCallback((error: AuthErrorResponse | undefined): void => updateSession({
    isAuthFailed: true,
    error: {
      title: error?.error?.title ?? null,
      description: error?.error?.description ?? null,
    },
  }), []);

  const onRefreshSessionFailed = useCallback((): void => updateSession({
    ...initialState,
    isAuthFailed: true,
  }), []);

  const onLoginSuccess = useCallback((data: AuthResponse): void => {
    if (isTwoFactorRequired(data)) return onTwoFactorRequired(data);
    if (isMissingCellPhoneNumber(data)) return onCellPhoneRequired(data);
    if (isFirstSignIn(data)) return onFirstSignInRequired();
    if (isAuthLoginResponse(data)) return onWaitingForUser(data);

    onError(data);
  }, []);

  const onRefreshSessionSuccess = useCallback((data: RefreshTokenResponse): void => {
    if (isTwoFactorRequired(data)) return onTwoFactorRequired(data);
    if (isMissingCellPhoneNumber(data)) return onCellPhoneRequired(data);
    if (isAuthLoginResponse(data)) return onWaitingForUser(data);

    onRefreshSessionFailed();
  }, []);

  const onCheck2FASuccess = useCallback((data: AuthCheck2FAResponse): void => {
    if (isAuthLoginResponse(data)) return onWaitingForUser(data);

    onError(data);
  }, []);

  const clearError = useCallback(() => updateSession({ error: { title: null, description: null } }), []);

  const resetSession = useCallback(() => updateSession(initialState), []);

  const onPlanSuccess = useCallback((plan: Plan): void => {
    updateSession({
      plan,
      isWaitingForPlan: false,
      isWaitingForCmsPages: true,
    });
  }, []);

  const onOrganizationSuccess = useCallback((organization: Organization): void => {
    updateSession({
      organization,
      isWaitingForOrganization: false,
      isWaitingForPlan: true,
    });
  }, []);

  const onUserOrPlanError = useCallback(() => {
    resetSession();
    onError({
      error: {
        code: "unknown_error",
        title: "Erreur",
        description: "Nous n'avons pas réussi à vous connecter. Veuillez réessayer.",
      },
    });
  }, []);

  const updateUserState = (updatedUserData: Partial<UserResponse["employee"]>) => {
    if (!session.user) return;
    updateSession({
      ...session,
      user: {
        employee: {
          ...session.user.employee,
          ...updatedUserData,
        },
      },
    });
  };

  const onCmsPagesSuccess = useCallback((): void => {
    updateSession({
      isWaitingForCmsPages: false,
      isAuthenticated: true,
      isAuthenticating: false,
    });
  }, []);

  const onCmsPagesError = useCallback(() => {
    updateSession({
      isWaitingForCmsPages: false,
      isAuthenticated: true,
      isAuthenticating: false,
    });
  }, []);

  return {
    session,
    onAuthenticating,
    onGetMeSuccess: onUserDataReceived,
    onLoginSuccess,
    onCheck2FASuccess,
    onTwoFactorRequired,
    onError,
    onRefreshSessionSuccess,
    onRefreshSessionFailed,
    clearError,
    resetSession,
    onPlanSuccess,
    onOrganizationSuccess,
    onUserOrPlanError,
    updateUserState,
    onCmsPagesSuccess,
    onCmsPagesError,
    onLegalComplianceCompleted,
  };
};
